/**
 * The three calls B1 and B2 make, typed from the generated contract.
 *
 * Nothing here hand-declares a payload — the shapes come from `api/generated/openapi.ts`,
 * which `npm run gen-client:check` diffs against `openapi.json` on every build
 * (MCK-20260521-002).
 *
 * TWO THINGS THE CONTRACT SAYS THAT THIS FILE IS BUILT AROUND
 *
 * 1. **There are no totals.** `Page` carries `items`, `next_cursor` and `has_more` and
 *    nothing else (`backend/src/kalqa/core/pagination.py`). Any count a screen shows is a
 *    count of what it has actually loaded, which is why `ProjectsPage` only shows one after
 *    the last page has arrived.
 * 2. **There is no `relation` filter.** `owned` / `shared` is computed per request from the
 *    caller's memberships (`projects/router.py:_response`) and is not a query parameter, so
 *    the ownership chips filter the loaded page in the browser while the status chips and the
 *    sort go to the server. See `queries.ts`.
 */

import { WORKSPACE_HEADER_NAME, api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

type Schemas = components['schemas'];

export type Project = Schemas['ProjectResponse'];
export type ProjectPage = Schemas['Page_ProjectResponse_'];
export type ProjectCreate = Schemas['ProjectCreateRequest'];

/** `active` / `archived` as the endpoint spells them. `all` is the absence of the parameter. */
export type ProjectStatus = Project['status'];
export type ProjectStatusFilter = ProjectStatus | 'all';

/** How the caller's own company relates to the project. Computed server-side, never stored. */
export type ProjectRelation = Project['relation'];
export type ProjectRelationFilter = ProjectRelation | 'all';

/**
 * The sort allowlist, spelled out because it *is* an allowlist: `service.list_projects`
 * refuses anything outside `created_at | updated_at | name`, and a `-` prefix is descending.
 *
 * TWIN: `SORTABLE` in `backend/src/kalqa/domains/projects/service.py`. A key added there is a
 * key to add here; a key removed there turns this one into a refusal.
 */
export type ProjectSort = '-updated_at' | 'name' | '-created_at';

/** B1's default. "Last activity" is what an architect opening the app is looking for. */
export const DEFAULT_SORT: ProjectSort = '-updated_at';

/**
 * How many cards one "Load more" adds. Three to four rows of the 280px grid on a normal
 * screen — enough that the first page fills the viewport, small enough that a firm with two
 * hundred projects does not pay for all of them to open the app.
 */
export const PAGE_SIZE = 24;

export interface ProjectListQuery {
  readonly status: ProjectStatusFilter;
  readonly sort: ProjectSort;
}

/**
 * One page of the list. `cursor` is `null` for the first.
 *
 * Absent keys and not `null` ones: `exactOptionalPropertyTypes` is on, and a `status=` with
 * an empty value is a different request from one with no `status` at all.
 */
export function listProjects(query: ProjectListQuery, cursor: string | null): Promise<ProjectPage> {
  return unwrap(
    api.GET('/api/v1/projects', {
      params: {
        query: {
          ...(query.status === 'all' ? {} : { status: query.status }),
          sort: query.sort,
          ...(cursor === null ? {} : { cursor }),
          limit: PAGE_SIZE,
        },
      },
    }),
  );
}

/**
 * Create a project, replay-safe.
 *
 * `Idempotency-Key` is accepted rather than demanded by the endpoint, and it is sent anyway.
 * The unique constraint on `(workspace_id, name)` already turns a double-click into a `409`,
 * but a `409` is a failure the person has to read and dismiss; a replayed key returns the
 * project that was created the first time. The key must be a UUID —
 * `core/idempotency.py:read_idempotency_key` parses it as one and answers `400` otherwise.
 *
 * The caller owns the key's lifetime, because the server fingerprints the body against it:
 * the same key with a different body is `409 idempotency_key_reused`, so a key survives a
 * retry of the same submission and no longer (see `NewProjectDrawer`).
 */
/**
 * Create a project, filed under one company.
 *
 * **`workspaceId` is explicit** (`KLQ-20260819-007` §3). The endpoint files the project under
 * whichever workspace the request acts as, and B1 spans memberships by design — so somebody
 * with two studios can be looking at one list and mean either company. An implicit answer
 * there is a project filed under the wrong firm, and a project's owning workspace is
 * immutable: there is no screen that moves it afterwards.
 *
 * The header set here beats the tab's default; `api/client.ts` checks for it before applying
 * one.
 */
export function createProject(
  body: ProjectCreate,
  idempotencyKey: string,
  workspaceId: string | null,
): Promise<Project> {
  return unwrap(
    api.POST('/api/v1/projects', {
      body,
      headers: {
        'Idempotency-Key': idempotencyKey,
        ...(workspaceId === null ? {} : { [WORKSPACE_HEADER_NAME]: workspaceId }),
      },
    }),
  );
}

/** One project. Every project screen reads it — the context bar carries its name and phase. */
export function readProject(projectId: string): Promise<Project> {
  return unwrap(api.GET('/api/v1/projects/{project_id}', { params: { path: { project_id: projectId } } }));
}

export type ProjectSurfaces = Schemas['ProjectSurfacesResponse'];
export type SurfaceRule = Schemas['SurfaceRuleResponse'];
export type SurfaceAccess = SurfaceRule['access'];

/**
 * Which screens this caller gets on this project, and read or write (`KLQ-20260819-004` §1).
 *
 * The tab row's contract. It is a **server** answer on purpose: `SURFACE_MATRIX` in
 * `backend/src/kalqa/core/authz.py` is keyed by role name and deliberately not monotonic in
 * the level ladder — the client role reads the activity log while the contractor does not —
 * so nothing the browser holds can compute it, and a copy of the table here would be a
 * drift surface no test could see.
 *
 * **It is navigation, not authorization.** Every endpoint behind every surface checks its
 * own authority per request; a wrong answer here costs a tab that 403s or a tab that is
 * missing, never access.
 */
export function readProjectSurfaces(projectId: string): Promise<ProjectSurfaces> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/surfaces', {
      params: { path: { project_id: projectId } },
    }),
  );
}

// --- Phases and the norm catalogue (`KLQ-20260827-002`, `KLQ-20260827-001`) ------------

export type ProjectPhase = Schemas['ProjectPhaseResponse'];
export type ProjectPhaseCreate = Schemas['ProjectPhaseCreateRequest'];
export type ProjectPhaseUpdate = Schemas['ProjectPhaseUpdateRequest'];

// The norm catalogue is NOT declared here. `features/setup` owns `Norm`, `NormVersion`,
// `NormPhase` and the one query that reads them (`KLQ-20260827-001`), and `features/norms`
// owns the studio's enabled set. Re-declaring either would be a second answer to one
// question — the failure `AppChrome`'s header describes for `GET /workspace`, in a domain
// where the resource is literally the same bytes for every tenant.

/**
 * The project's enabled phases, in the norm's order, **archived ones included**.
 *
 * The server does not offer an `archived` filter and this file does not invent one: an
 * archived phase still has drawings filed under it, so a bar that hid it would hide the only
 * way back to them. `PhaseBar` greys it instead — see `phase.ts` for why greyed still means
 * selectable.
 */
export function listProjectPhases(projectId: string): Promise<ProjectPhase[]> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/phases', {
      params: { path: { project_id: projectId } },
    }),
  );
}

export function createProjectPhase(
  projectId: string,
  body: ProjectPhaseCreate,
): Promise<ProjectPhase> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/phases', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

export function updateProjectPhase(
  projectId: string,
  phaseId: string,
  body: ProjectPhaseUpdate,
): Promise<ProjectPhase> {
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/phases/{phase_id}', {
      params: { path: { project_id: projectId, phase_id: phaseId } },
      body,
    }),
  );
}

/** `204`, or `409` when a drawing is filed under the phase — then it archives instead. */
export function deleteProjectPhase(projectId: string, phaseId: string): Promise<undefined> {
  return unwrap(
    api.DELETE('/api/v1/projects/{project_id}/phases/{phase_id}', {
      params: { path: { project_id: projectId, phase_id: phaseId } },
    }),
  );
}

/**
 * The phase a new drawing lands in. **Not the viewer's selection** — that never leaves the
 * browser (`phase.ts`); this is a project-level default every member of the project sees.
 */
export function setCurrentPhase(projectId: string, phaseId: string | null): Promise<undefined> {
  return unwrap(
    api.PUT('/api/v1/projects/{project_id}/current-phase', {
      params: { path: { project_id: projectId } },
      body: { phase_id: phaseId },
    }),
  );
}

// --- The object breakdown (`KLQ-20260817-002`) ----------------------------------------

export type ProjectObject = Schemas['ProjectObjectResponse'];
export type ProjectObjectCreate = Schemas['ProjectObjectCreateRequest'];
export type ProjectObjectUpdate = Schemas['ProjectObjectUpdateRequest'];
export type ObjectKind = ProjectObject['kind'];

/**
 * The project's objektová skladba — main structure first, then by kind and number.
 *
 * **Flat, and joined on `parent_id` by whoever renders it.** The server returns a list rather
 * than a tree because a composition is three levels at most and tens of rows, and a nested
 * response would need a different shape per depth to save one grouping pass.
 *
 * Archived rows are excluded by default. A drawing filed under a superseded object still
 * renders its name from that row, so anything that has to *explain* an existing identifier
 * asks for them; a picker of what to file under next does not.
 */
export function listProjectObjects(
  projectId: string,
  includeArchived = false,
): Promise<ProjectObject[]> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/objects', {
      params: {
        path: { project_id: projectId },
        query: { include_archived: includeArchived },
      },
    }),
  );
}

/**
 * Add one stavebný objekt or prevádzkový súbor.
 *
 * `422` for a shape the statute has no room for — an unknown category band, a parent of the
 * other kind, a nesting deeper than the kind allows. `409` for a number, an annex-24 file code
 * or the main flag this project has already spent.
 */
export function createProjectObject(
  projectId: string,
  body: ProjectObjectCreate,
): Promise<ProjectObject> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/objects', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/**
 * Edit one object, subject to the lock.
 *
 * `kind` and `parent_id` are not on this request and cannot be: each would reach a rule that
 * is only checked on the way in. A `409` here is not a race — it is the vocabulary rule of
 * `MCK-20260813-003` §2 saying this number is already printed on a sheet.
 */
export function updateProjectObject(
  projectId: string,
  objectId: string,
  body: ProjectObjectUpdate,
): Promise<ProjectObject> {
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/objects/{object_id}', {
      params: { path: { project_id: projectId, object_id: objectId } },
      body,
    }),
  );
}

/**
 * Grey out one object. Idempotent, and it deletes nothing.
 *
 * There is no delete, on purpose: a slot's `object_id` is `ON DELETE RESTRICT` and an object's
 * number is rendered into published identifiers. Archiving the main object frees the main flag
 * for a successor without unsetting it on history.
 */
export function archiveProjectObject(projectId: string, objectId: string): Promise<ProjectObject> {
  // `archived: true` on the PATCH, not a `POST .../archive` (server change, 2026-08-28): that
  // path nested three collections deep and the route-inventory guardrail refuses it. Same shape
  // as archiving a phase rung. Still its own function here, because the *call site* asks a
  // different question — "retire this object" is not "edit these fields".
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/objects/{object_id}', {
      params: { path: { project_id: projectId, object_id: objectId } },
      body: { archived: true },
    }),
  );
}

